import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MainLayout } from "@/components/layout/MainLayout";
import { motion } from "framer-motion";
import {
  Mail,
  Bell,
  QrCode,
  User,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { getUserSettings } from "@/lib/userSettings";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/hooks/useTranslation";

interface CaregiverInfo {
  phone: string;
  email: string;
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

export default function Contacts() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { t, dir, isRTL, lang } = useTranslation();
  const [caregiverInfo, setCaregiverInfo] = useState<CaregiverInfo>({ phone: "", email: "" });

  // Check for initial message from sessionStorage
  useEffect(() => {
    const sendMessageText = sessionStorage.getItem("sendMessageText");
    const action = searchParams.get("action");
    
    if (sendMessageText && action === "send") {
      sessionStorage.removeItem("sendMessageText");
      // Navigate to compose page for email
      navigate(`/contacts/compose?type=email&recipient=${encodeURIComponent(caregiverInfo.email)}`);
    }
  }, [searchParams, navigate, caregiverInfo.email]);

  useEffect(() => {
    const saved = localStorage.getItem("ayuni-caregiver");
    if (saved) {
      setCaregiverInfo(JSON.parse(saved));
    }
  }, []);

  const openSettings = () => {
    navigate("/contacts/settings");
  };

  // handleSendMessage moved to ContactsCompose page

  const openComposer = (type: "email" | "sms" | "whatsapp") => {
    // Navigate to compose page instead of opening modal
    navigate(`/contacts/compose?type=${type}&recipient=${encodeURIComponent(type === "email" ? caregiverInfo.email : caregiverInfo.phone)}`);
  };

  const handleGoToQRSettings = () => {
    navigate('/settings');
  };

  const handleEditEmail = () => {
    navigate("/contacts/settings");
  };

  const handleSendPushNotification = async (message: string) => {
    try {
      const { supabase } = await import('@/integrations/supabase/client');
      const { data: userData } = await supabase.auth.getUser();
      
      if (!userData.user?.id) {
        toast({
          title: t("error"),
          description: t("pleaseLoginFirst"),
          variant: "destructive",
        });
        return;
      }
      
      const { data, error } = await supabase.functions.invoke('send-push-alert', {
        body: {
          patientId: userData.user.id,
          alertType: 'help',
          message: message.trim() || undefined
        }
      });
      
      if (error) throw error;
      
      if (data?.sent > 0) {
        toast({
          title: t("notificationSent"),
          description: t("sentToDevices").replace("{count}", String(data.sent)),
        });
      } else {
        toast({
          title: t("noCaregiversConnected"),
          description: t("scanQRInSettings"),
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: t("error"),
        description: t("failedToSendNotification"),
        variant: "destructive",
      });
    }
  };

  const openPushComposer = () => {
    // Navigate to compose page for push notifications
    navigate("/contacts/compose?type=push");
  };

  const communicationOptions = [
    {
      id: "email",
      label: t("email"),
      icon: Mail,
      color: "bg-ayuni-teal/20 hover:bg-ayuni-teal/30 border-ayuni-teal/40",
      iconColor: "text-ayuni-teal",
      onClick: () => openComposer("email"),
    },
    {
      id: "push",
      label: t("pushAlert"),
      icon: Bell,
      color: "bg-ayuni-coral/20 hover:bg-ayuni-coral/30 border-ayuni-coral/40",
      iconColor: "text-ayuni-coral",
      onClick: openPushComposer,
    },
  ];

  return (
    <MainLayout>
      <div className="flex-1 flex flex-col p-6 overflow-auto" dir={dir}>
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-foreground">{t("contactsTitle")}</h1>
        </div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="flex flex-col gap-6 max-w-4xl mx-auto w-full"
        >
          {/* Communication Options Grid */}
          <div className="grid grid-cols-2 gap-4 p-2 rounded-3xl transition-all">
            {communicationOptions.map((option) => (
                <motion.button
                  key={option.id}
                  variants={itemVariants}
                  onClick={option.onClick}
                  data-scan-item
                  className={cn(
                    "aspect-square rounded-3xl border-2 flex flex-col items-center justify-center gap-4 transition-all hover:scale-105",
                    option.color
                  )}
                >
                  <option.icon className={`w-16 h-16 ${option.iconColor}`} />
                  <span className="text-xl font-medium text-foreground">{option.label}</span>
                </motion.button>
              ))}
          </div>

          {/* Caregiver Section */}
          <motion.div variants={itemVariants} className="mt-4">
            <div className="flex items-center gap-2 mb-4">
              <User className="w-5 h-5 text-muted-foreground" />
              <h2 className="text-lg font-medium text-muted-foreground">{t("caregiverDetails")}</h2>
            </div>
            
            {/* Caregiver buttons (email edit + QR settings) */}
            <div className="grid grid-cols-2 gap-4 p-2 rounded-2xl transition-all">
              <button
                onClick={handleEditEmail}
                data-scan-item
                className="p-4 rounded-2xl border-2 border-accent/30 bg-accent/10 hover:bg-accent/20 transition-colors flex items-center gap-3"
              >
                <Mail className="w-6 h-6 text-accent" />
                <div className={isRTL ? "text-right" : "text-left"}>
                  <p className="text-sm text-muted-foreground">{t("email")}</p>
                  <p className="text-foreground font-medium truncate">
                    {caregiverInfo.email || t("notSet")}
                  </p>
                </div>
              </button>

              <button
                onClick={handleGoToQRSettings}
                data-scan-item
                className="p-4 rounded-2xl border-2 border-primary/30 bg-primary/10 hover:bg-primary/20 transition-colors flex items-center gap-3"
              >
                <QrCode className="w-6 h-6 text-primary" />
                <div className={isRTL ? "text-right" : "text-left"}>
                  <p className="text-sm text-muted-foreground">{t("pushAlert")}</p>
                  <p className="text-foreground font-medium truncate">
                    {t("goToSettings")}
                  </p>
                </div>
              </button>
            </div>
          </motion.div>
        </motion.div>
        
      </div>
    </MainLayout>
  );
}
